import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        username: formData.username.trim(),
        password: formData.password,
      };
      const response = await axios.post(`${API}/admin/login`, payload);
      toast.success('Welcome Admin!');
      onLogin(response.data);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data?.detail || 'Invalid credentials');
      } else if (error.request) {
        toast.error('Cannot reach server. Check your internet connection.');
      } else {
        toast.error('Login failed: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#09090b] p-5 pt-safe sm:p-6">
      <div className="pointer-events-none absolute -top-32 left-1/2 h-72 w-[140%] -translate-x-1/2 rounded-full bg-[#A855F7]/10 blur-3xl" />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative w-full max-w-md"
      >
        <div className="rounded-3xl border border-white/[0.06] bg-[#18181b] p-6 sm:p-8">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#A855F7]/15">
              <Shield className="h-6 w-6 text-[#A855F7]" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#A855F7]">Raffles Я Us</p>
              <h2 className="font-heading text-xl font-semibold tracking-tight text-white sm:text-2xl">Admin portal</h2>
            </div>
          </div>
          <p className="mt-4 text-sm text-[#a1a1aa]">Log in to manage draws, tickets and participants.</p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="username" className="text-xs font-semibold uppercase tracking-wider text-[#a1a1aa]">Username</Label>
              <Input
                id="username"
                data-testid="admin-username-input"
                placeholder="Admin username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="off"
                className="h-12 rounded-xl border-white/[0.08] bg-[#09090b] text-white placeholder:text-[#52525b] focus-visible:ring-[#A855F7]"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-[#a1a1aa]">Password</Label>
              <Input
                id="password"
                data-testid="admin-password-input"
                type="password"
                placeholder="Enter password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="current-password"
                className="h-12 rounded-xl border-white/[0.08] bg-[#09090b] text-white placeholder:text-[#52525b] focus-visible:ring-[#A855F7]"
              />
            </div>

            <Button
              type="submit"
              data-testid="admin-login-submit-btn"
              disabled={loading}
              className="mt-2 h-12 w-full rounded-full bg-[#A855F7] font-semibold text-white hover:bg-[#9333EA] active:scale-[0.98]"
            >
              {loading ? 'Logging in…' : 'Log in as admin'}
            </Button>
          </form>

          <div className="mt-5 border-t border-white/[0.06] pt-4 text-center">
            <Link
              to="/"
              data-testid="back-to-user-login-link"
              className="inline-flex items-center gap-2 text-sm text-[#a1a1aa] transition-colors hover:text-[#EAB308]"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to member login
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
